// src/components/Registros/forms/PermisoForm.jsx
import React, { useState } from 'react';
import { Container, Form, Button, Row, Col, Alert } from 'react-bootstrap';
import { FaArrowLeft, FaSave, FaTimes } from 'react-icons/fa';
import Swal from 'sweetalert2';
import { useNavigate } from 'react-router-dom';
import SelectMult from '../../SettingNot/SelectMult';
import AdjuntosRegistro from '../AdjuntosRegistro';
import { crearDatos } from '../../../api/common';

const PermisoForm = ({ onClose, onSubmit, tipoRegistro, solicitante }) => {
  const navigate = useNavigate();
  const [formData, setFormData] = useState({
    solicitante: solicitante,
    titulo: '',
    tipoPermiso: '',
    motivoPermiso: '',
    fechaInicio: '',
    fechaFin: '',
    horaInicio: '',
    horaFin: '',
    descripcion: '',
    responsablesAprobacion: [],
    adjuntos: []
  });

  const [errors, setErrors] = useState({});
  const [loading, setLoading] = useState(false);

  // Data de prueba para responsables
  const responsables = [
    { id: '1', nombre: 'Juan Pérez', cargo: 'Gerente General' },
    { id: '2', nombre: 'María García', cargo: 'Jefe de RRHH' },
    { id: '3', nombre: 'Carlos López', cargo: 'Supervisor' }
  ];

  const tiposPermiso = [
    'personal',
    'medico',
    'familiar',
    'calamidad',
    'estudio',
    'otro'
  ];

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
    
    // Limpiar error del campo
    if (errors[name]) {
      setErrors(prev => ({
        ...prev,
        [name]: ''
      }));
    }
  };

  const handleResponsableChange = (e) => {
    const { value } = e.target; // value es un array de IDs: ['id1', 'id2', ...]
    
    // Transformar el array de IDs a objetos con la estructura del modelo
    const responsablesFormateados = value?.map((usuarioId, index) => ({
      usuario: usuarioId,
      orden: index + 1,
      estado: 'pendiente'
    }));
    setFormData(prev => ({
      ...prev,
      responsablesAprobacion: responsablesFormateados
    }));
  };

  const validateForm = () => {
    const newErrors = {};

    // Título se autogenera a partir del tipoPermiso (ver handleSubmit).
    if (!formData.tipoPermiso) {
      newErrors.tipoPermiso = 'Debe seleccionar un tipo de permiso';
    }

    if (!formData.motivoPermiso.trim()) {
      newErrors.motivoPermiso = 'El motivo es requerido';
    }

    if (!formData.fechaInicio) {
      newErrors.fechaInicio = 'La fecha de inicio es requerida';
    }

    if (!formData.fechaFin) {
      newErrors.fechaFin = 'La fecha de fin es requerida';
    }

    if (formData.fechaInicio && formData.fechaFin) {
      if (new Date(formData.fechaFin) < new Date(formData.fechaInicio)) {
        newErrors.fechaFin = 'La fecha fin debe ser mayor o igual a la fecha inicio';
      }
    }

    if (formData.horaInicio && formData.horaFin) {
      if (formData.fechaInicio === formData.fechaFin) {
        if (formData.horaFin <= formData.horaInicio) {
          newErrors.horaFin = 'La hora fin debe ser mayor a la hora inicio';
        }
      }
    }

    if (formData.responsablesAprobacion.length === 0) {
      newErrors.responsablesAprobacion = 'Debe seleccionar al menos un responsable';
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (!validateForm()) {
      Swal.fire({
        icon: 'error',
        title: 'Errores en el formulario',
        text: 'Por favor, corrija los errores antes de continuar',
        confirmButtonColor: '#0d6efd'
      });
      return;
    }

    setLoading(true);

    try {
      const tipoLabel = formData.tipoPermiso
        ? formData.tipoPermiso.charAt(0).toUpperCase() + formData.tipoPermiso.slice(1)
        : '';
      const tituloAuto = `Permiso ${tipoLabel}`.trim();
      const dataToSubmit = {
        ...formData,
        titulo: tituloAuto,
        tipoSolicitud: 'permiso',
        causaInactividad: true
      };

      await crearDatos('Registro', dataToSubmit);

      await Swal.fire({
        icon: 'success',
        title: 'Solicitud Enviada',
        text: 'Tu solicitud de permiso ha sido registrada correctamente',
        confirmButtonColor: '#198754'
      });
      if (onSubmit) onSubmit(dataToSubmit);
      navigate('/Solicitudes');
    } catch (error) {
      Swal.fire({
        icon: 'error',
        title: 'Error',
        text: 'Hubo un error al enviar la solicitud. Por favor, intenta nuevamente.',
        confirmButtonColor: '#dc3545'
      });
    } finally {
      setLoading(false);
    }
  };

  const handleCancel = () => {
    Swal.fire({
      title: '¿Estás seguro?',
      text: 'Los datos ingresados se perderán',
      icon: 'warning',
      showCancelButton: true,
      confirmButtonColor: '#dc3545',
      cancelButtonColor: '#6c757d',
      confirmButtonText: 'Sí, cancelar',
      cancelButtonText: 'No, continuar'
    }).then((result) => {
      if (result.isConfirmed) {
        onClose();
      }
    });
  };

  return (
    <Container fluid className="py-4" style={{ background: 'linear-gradient(135deg, #f5f7fa 0%, #c3cfe2 100%)', minHeight: '100vh' }}>
      <div className="form-container">
        <div className="form-header" style={{ background: 'linear-gradient(135deg, #0d6efd 0%, #0a58ca 100%)' }}>
          <div className="d-flex align-items-center">
            <Button 
              variant="light" 
              size="sm" 
              onClick={handleCancel}
              className="me-3"
            >
              <FaArrowLeft />
            </Button>
            <div>
              <h3 className="mb-0 fw-bold">Solicitud de Permiso</h3>
              <p className="mb-0 opacity-75">Complete el formulario para solicitar un permiso</p>
            </div>
          </div>
        </div>

        <div className="form-body">
          <Form onSubmit={handleSubmit}>
            {/* Información Básica */}
            <div className="form-section">
              <h5 className="form-section-title">Información Básica</h5>
              
              <Row className="mb-3">
                <Col md={6}>
                  <Form.Group>
                    <Form.Label className="fw-bold">Tipo de Permiso *</Form.Label>
                    <Form.Select
                      name="tipoPermiso"
                      value={formData.tipoPermiso}
                      onChange={handleChange}
                      isInvalid={!!errors.tipoPermiso}
                    >
                      <option value="">Seleccione un tipo</option>
                      {tiposPermiso.map(tipo => (
                        <option key={tipo} value={tipo}>
                          {tipo.charAt(0).toUpperCase() + tipo.slice(1)}
                        </option>
                      ))}
                    </Form.Select>
                    <Form.Control.Feedback type="invalid">
                      {errors.tipoPermiso}
                    </Form.Control.Feedback>
                  </Form.Group>
                </Col>
                <Col md={6}>
                  <Form.Group>
                    <Form.Label className="fw-bold">Motivo del Permiso *</Form.Label>
                    <Form.Control
                      type="text"
                      name="motivoPermiso"
                      value={formData.motivoPermiso}
                      onChange={handleChange}
                      placeholder="Motivo breve"
                      isInvalid={!!errors.motivoPermiso}
                    />
                    <Form.Control.Feedback type="invalid">
                      {errors.motivoPermiso}
                    </Form.Control.Feedback>
                  </Form.Group>
                </Col>
              </Row>
            </div>

            {/* Fechas y Horarios */}
            <div className="form-section">
              <h5 className="form-section-title">Fechas y Horarios</h5>
              
              <Row className="mb-3">
                <Col md={6}>
                  <Form.Group>
                    <Form.Label className="fw-bold">Fecha de Inicio *</Form.Label>
                    <Form.Control
                      type="date"
                      name="fechaInicio"
                      value={formData.fechaInicio}
                      onChange={handleChange}
                      isInvalid={!!errors.fechaInicio}
                    />
                    <Form.Control.Feedback type="invalid">
                      {errors.fechaInicio}
                    </Form.Control.Feedback>
                  </Form.Group>
                </Col>
                <Col md={6}>
                  <Form.Group>
                    <Form.Label className="fw-bold">Fecha de Fin *</Form.Label>
                    <Form.Control
                      type="date"
                      name="fechaFin"
                      value={formData.fechaFin}
                      onChange={handleChange}
                      isInvalid={!!errors.fechaFin}
                    />
                    <Form.Control.Feedback type="invalid">
                      {errors.fechaFin}
                    </Form.Control.Feedback>
                  </Form.Group>
                </Col>
              </Row>

              <Row className="mb-3">
                <Col md={6}>
                  <Form.Group>
                    <Form.Label className="fw-bold">Hora de Inicio</Form.Label>
                    <Form.Control
                      type="time"
                      name="horaInicio"
                      value={formData.horaInicio}
                      onChange={handleChange}
                    />
                    <Form.Text className="text-muted">
                      Opcional, solo si es permiso por horas
                    </Form.Text>
                  </Form.Group>
                </Col>
                <Col md={6}>
                  <Form.Group>
                    <Form.Label className="fw-bold">Hora de Fin</Form.Label>
                    <Form.Control
                      type="time"
                      name="horaFin"
                      value={formData.horaFin}
                      onChange={handleChange}
                      isInvalid={!!errors.horaFin}
                    />
                    <Form.Control.Feedback type="invalid">
                      {errors.horaFin}
                    </Form.Control.Feedback>
                  </Form.Group>
                </Col>
              </Row>
            </div>

            {/* Descripción */}
            <div className="form-section">
              <h5 className="form-section-title">Descripción Detallada</h5>
              
              <Form.Group className="mb-3">
                <Form.Label className="fw-bold">Descripción</Form.Label>
                <Form.Control
                  as="textarea"
                  rows={4}
                  name="descripcion"
                  value={formData.descripcion}
                  onChange={handleChange}
                  placeholder="Describa con detalle el motivo de su permiso..."
                  maxLength={500}
                />
                <Form.Text className="text-muted">
                  {formData.descripcion.length}/500 caracteres
                </Form.Text>
              </Form.Group>
            </div>

            {/* Responsables de Aprobación */}
            <div className="form-section">
              <h5 className="form-section-title">Responsables de Aprobación</h5>
              
              <Form.Group>
                <Form.Label className="fw-bold">Seleccione los responsables que deben aprobar *</Form.Label>
                <SelectMult
                //dataDefault={itemData?.userAutorizados}
                handledata={handleResponsableChange}
                name='responsablesAprobacion'
                collection={"Usuarios"}
                field={"nombre"}
                baseFilter={[ // Body para la consuta aggregate de mongo para metrologos y que esten activos.
                        {
                            "$match": {
                            'status': 'activo'
                            } 
                        },
                        {
                            "$lookup": {
                            "from": "roles",
                            "localField": "role",
                            "foreignField": "_id",
                            "as": "result"
                            }
                        },
                        {
                            "$match": {
                            "result.nombre": { $ne: "Cliente" } 
                            }
                        }
                        ]}
                />
                {errors.responsablesAprobacion && (
                  <Alert variant="danger" className="mt-2 py-2">
                    {errors.responsablesAprobacion}
                  </Alert>
                )}
              </Form.Group>
            </div>

            <div className="form-section">
              <h5 className="form-section-title">Adjuntos</h5>
              <AdjuntosRegistro
                tipoSolicitud="permiso"
                adjuntos={formData.adjuntos}
                onChange={(arr) => setFormData(prev => ({ ...prev, adjuntos: arr }))}
                disabled={loading}
              />
            </div>

            {/* Botones */}
            <div className="d-flex justify-content-end gap-2 mt-4">
              <Button
                variant="secondary"
                onClick={handleCancel}
                disabled={loading}
                className="px-4"
              >
                <FaTimes className="me-2" />
                Cancelar
              </Button>
              <Button 
                variant="primary" 
                type="submit"
                disabled={loading}
                className="px-4"
              >
                {loading ? (
                  <>
                    <span className="spinner-border spinner-border-sm me-2" />
                    Enviando...
                  </>
                ) : (
                  <>
                    <FaSave className="me-2" />
                    Enviar Solicitud
                  </>
                )}
              </Button>
            </div>
          </Form>
        </div>
      </div>
    </Container>
  );
};

export default PermisoForm;